<script setup lang="ts">
import { AbcIcon } from "@ordbogen-npm/abc-dsk";
import { storeToRefs } from "pinia";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import AudioButton from "@/components/AudioButton.vue";
import { useVocabStore } from "@/stores";
import type { WordClass } from "@/types";

import WordListItemDeleteModal from "./WordListItemDeleteModal.vue";

const props = defineProps<{
  locked?: boolean;
  deletable?: boolean;
  sourceWord: string;
  targetWord: string;
  wordClass: WordClass;
  sourceWordPronunciationUrl: string | null;
}>();

const emit = defineEmits<{
  delete: [];
  unlock: [];
}>();

const { t } = useI18n();
const store = useVocabStore();

const { forceDeleteWord } = storeToRefs(store);

const isModalOpen = ref<boolean>(false);

const onRemove = (force = false) => {
  if (forceDeleteWord.value || force) {
    isModalOpen.value = false;
    emit("delete");
    return;
  }

  isModalOpen.value = true;
};
</script>

<template>
  <article class="word-list-item" :aria-label="t('vocab.vocabList.wordListItem.label')">
    <div class="word-list-item__text-wrapper" :inert="props.locked">
      <p class="word-list-item__word-class">{{ props.wordClass }}</p>
      <p
        class="word-list-item__word word-list-item__source-word"
        lang="en"
        :title="props.sourceWord"
      >
        <span class="word-list-item__source-word-text">{{ props.sourceWord }}</span>
        <AudioButton
          v-if="props.sourceWordPronunciationUrl !== null"
          :audio-url="props.sourceWordPronunciationUrl"
        />
      </p>
      <p
        class="word-list-item__word word-list-item__target-word"
        lang="da"
        :title="props.targetWord"
      >
        {{ props.targetWord }}
      </p>
    </div>
    <button v-if="props.deletable" class="word-list-item__delete-btn" @click="onRemove()">
      {{ t("vocab.vocabList.wordListItem.remove") }}
    </button>
    <WordListItemDeleteModal
      v-if="props.deletable"
      :is-open="isModalOpen"
      :word="props.sourceWord"
      @delete="onRemove(true)"
      @cancel="isModalOpen = false"
    />
    <Transition name="fade">
      <button v-if="props.locked" class="unlock-overlay" @click="emit('unlock')">
        <AbcIcon name="unlock" color="white" />
        <span class="unlock-overlay__label">
          {{ t("vocab.vocabList.wordListItem.unlockLabel") }}
        </span>
      </button>
    </Transition>
  </article>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.word-list-item {
  display: flex;
  position: relative;
  align-items: center;
  height: 100%;
  padding: 18px 20px;
  overflow: hidden;
  border-radius: 8px;
  background: rgb(@theme-colors[@grey][25]);

  &:hover .word-list-item__delete-btn {
    clip-path: inset(0 0 0 0);
  }
}

.word-list-item__text-wrapper {
  display: flex;
  position: relative;
  flex-direction: column;
  max-width: 100%;
  padding: 14px 0;
  transform: translateY(0);
}

.word-list-item__word-class {
  margin: 0;
  color: rgb(@theme-colors[@grey][base]);
  font-family: RubikVariable, sans-serif;
  font-size: 0.75rem;
  font-style: italic;
  font-weight: 400;
}

.word-list-item__word {
  margin: 0;
  font-size: 1rem;
  line-height: 1.5rem;
}

.word-list-item__source-word {
  display: flex;
  align-items: center;
  gap: 6px;

  &-text {
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.word-list-item__target-word {
  overflow: hidden;
  color: rgb(@theme-colors[@grey][base]);
  line-height: 1.25rem;
  text-overflow: ellipsis;
  word-break: break-word;
  white-space: nowrap;
  hyphens: auto;
}

.word-list-item__delete-btn {
  display: block;
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0px;
  width: 100%;
  height: 30px;
  padding: 6px 20px;
  clip-path: inset(0 0 0 0);
  transition:
    background 150ms ease-in-out,
    150ms clip-path ease-in-out;
  border: none;
  border-radius: 0px 0px 8px 8px;
  background: rgb(@theme-colors[@red][base]);
  color: @colors[@white];
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.125rem;
  text-align: left;
  text-transform: uppercase;
  will-change: transform;

  #media-breakpoint-up(md, {
    clip-path: inset(30px 0 0 0);
  });

  &:hover {
    background: rgb(@theme-colors[@red][110]);
  }

  &:focus {
    clip-path: inset(0 0 0 0);
    background: rgb(@theme-colors[@red][110]);
  }
}

.unlock-overlay {
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  transition: all 150ms ease-in-out;
  border: none;
  border-radius: 8px;
  outline-offset: -5px;
  background: rgb(@theme-colors[@primary][base]);
  color: @colors[@white];

  &:hover,
  &:focus {
    background: rgb(@theme-colors[@primary][110]);
  }

  &__label {
    margin-left: 6px;
    font-size: 1rem;
    font-weight: 600;
    letter-spacing: 0.0688rem;
    line-height: 1.1875rem;
    text-transform: uppercase;
  }
}
</style>
